import { useCallback, useEffect, useState } from 'react'

import { getApplicationRouting, postPortalCopy } from 'api/application'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import ApplicationPortal from 'state/enums/ApplicationPortal'

interface PortalCopyState {
    redirectUrl: string
}

const STORAGE_KEY_PREFIX = 'portal_copy_'

function readFromStorage(key: string): PortalCopyState | null {
    try {
        const stored = localStorage.getItem(key)

        return stored ? (JSON.parse(stored) as PortalCopyState) : null
    } catch {
        localStorage.removeItem(key)

        return null
    }
}

export interface UsePortalCopyResult {
    hasCopy: boolean
    isLoading: boolean
    triggerCopy: () => Promise<string | null>
}

export default function usePortalCopy(
    applicationUuid: string,
    targetPortal: ApplicationPortal,
): UsePortalCopyResult {
    const storageKey = `${STORAGE_KEY_PREFIX}${applicationUuid}`

    const [copyState, setCopyState] = useState<PortalCopyState | null>(() =>
        readFromStorage(storageKey),
    )

    const [isLoading, setIsLoading] = useState(false)

    // localStorage is only a fast-path cache; the routing record on the
    // application is the source of truth, so reconcile against it on
    // mount rather than trusting a cache entry that never expires.
    useEffect(() => {
        let cancelled = false

        const verifyAgainstBackend = async () => {
            try {
                const routingRecord = await getApplicationRouting(
                    applicationUuid,
                    targetPortal,
                )

                if (cancelled) return

                const backendUrl =
                    routingRecord?.portal === targetPortal
                        ? (routingRecord.redirectUrl ??
                          routingRecord.dealerWebAppUrl)
                        : null

                if (!backendUrl) {
                    localStorage.removeItem(storageKey)
                    setCopyState(null)

                    return
                }

                setCopyState((current) => {
                    if (current) return current

                    const newState: PortalCopyState = {
                        redirectUrl: backendUrl,
                    }

                    localStorage.setItem(storageKey, JSON.stringify(newState))

                    return newState
                })
            } catch (error) {
                if (cancelled) return

                OctaneLogger.error(
                    'Error verifying portal copy state against routing records.',
                    undefined,
                    error as Error,
                )
            }
        }

        verifyAgainstBackend()

        return () => {
            cancelled = true
        }
    }, [applicationUuid, targetPortal, storageKey])

    const triggerCopy = useCallback(async (): Promise<string | null> => {
        if (isLoading) return null

        if (copyState) return copyState.redirectUrl

        setIsLoading(true)
        try {
            const result = await postPortalCopy(applicationUuid, targetPortal)

            const state: PortalCopyState = {
                redirectUrl: result.redirectUrl ?? result.dealerWebAppUrl,
            }

            setCopyState(state)
            localStorage.setItem(storageKey, JSON.stringify(state))

            return state.redirectUrl
        } catch (error) {
            OctaneLogger.error(
                'Error creating portal copy for jump CTA.',
                undefined,
                error as Error,
            )

            return null
        } finally {
            setIsLoading(false)
        }
    }, [applicationUuid, targetPortal, storageKey, copyState, isLoading])

    return {
        hasCopy: !!copyState,
        isLoading,
        triggerCopy,
    }
}
